import { EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { createFeature, createReducer, on } from '@ngrx/store';

import * as NotificationActions from './notification.actions';
import { Notification, NotificationState } from '../models/notification.model';

export interface Application {
  ApplicationKey: string;
  Name: string;
  Locales: string[];
  Areas: string[];
}

export const AVAILABLE_APPLICATIONS: Application[] = [
  {
    ApplicationKey: 'Cart',
    Locales: ['nl-NL', 'fr-FR', 'en-US', 'pl-PL', 'de-DE'],
    Name: 'Webshop',
    Areas: ['DSC'],
  },
  {
    ApplicationKey: 'DairyResults',
    Name: 'Dairy Results',
    Locales: ['nl-NL', 'en-US'],
    Areas: ['RPP'],
  },
  {
    ApplicationKey: 'DairyStartMonitor',
    Name: 'Dairy Start Monitor',
    Locales: ['nl-NL', 'en-US'],
    Areas: ['RPP'],
  },
  {
    ApplicationKey: 'LabResult',
    Name: 'Lab Result',
    Locales: ['nl-NL', 'fr-FR', 'en-US'],
    Areas: ['RPP', 'PBS'],
  },
  {
    ApplicationKey: 'Layer',
    Name: 'Layers',
    Locales: ['nl-NL', 'nl-BE', 'fr-BE', 'en-US'],
    Areas: ['RPP'],
  },
  {
    ApplicationKey: 'Mixzen',
    Name: 'Mixzen',
    Locales: ['nl-NL', 'en-US'],
    Areas: ['RPP'],
  },
  {
    ApplicationKey: 'CustomerDashboard',
    Name: 'My Site',
    Locales: ['nl-NL', 'fr-FR', 'en-US', 'pl-PL', 'de-DE'],
    Areas: ['DSC'],
  },
  {
    ApplicationKey: 'Pig',
    Name: 'Pig',
    Locales: ['nl-NL', 'en-US'],
    Areas: ['RPP'],
  },
  {
    ApplicationKey: 'PoultryNEXT',
    Name: 'Poultry NEXT',
    Locales: ['nl-NL', 'nl-BE', 'fr-BE', 'en-US', 'pl-PL', 'de-DE', 'hu-HU', 'ar-JO'],
    Areas: ['RPP'],
  },
  {
    ApplicationKey: 'PoultryNEXTRearing',
    Name: 'Poultry NEXT Rearing',
    Locales: ['nl-NL', 'en-US'],
    Areas: ['RPP'],
  },
  {
    ApplicationKey: 'RoughagePlus',
    Name: 'Ruwvoer+ admin',
    Locales: ['nl-NL', 'en-US'],
    Areas: ['RPP'],
  },
  {
    ApplicationKey: 'NextAdmin',
    Name: 'Next Admin',
    Locales: ['nl-NL'],
    Areas: ['Platform'],
  },
  {
    ApplicationKey: 'Management',
    Name: 'Management',
    Locales: ['nl-NL', 'en-US', 'pl-PL'],
    Areas: ['Platform'],
  },
  {
    ApplicationKey: 'Profile',
    Name: 'Profile',
    Locales: ['nl-NL', 'en-US'],
    Areas: ['Platform'],
  },
];

export const NOTIFICATION_APPLICATION_KEYS = AVAILABLE_APPLICATIONS.map((app) => app.ApplicationKey);
export type NotificationApplicationKey = (typeof NOTIFICATION_APPLICATION_KEYS)[number];

export const adapter: EntityAdapter<Notification> = createEntityAdapter<Notification>();

export const initialState: NotificationState = {
  notifications: adapter.getInitialState([]),
  salesAreas: [],
  loading: false,
  applicationKeys: NOTIFICATION_APPLICATION_KEYS,
  deletingNotificationId: null,
};

export const notificationFeature = createFeature({
  name: 'notification-management',
  reducer: createReducer(
    initialState,
    on(NotificationActions.loadNotificationsRequest, (state) => ({
      ...state,
      loading: true,
    })),
    on(NotificationActions.loadNotificationsSuccess, (state, { notifications }) => ({
      ...state,
      notifications: adapter.setAll(notifications, state.notifications),
      loading: false,
    })),
    on(NotificationActions.loadNotificationsFailure, (state) => ({
      ...state,
      loading: false,
    })),
    on(NotificationActions.upsertNotificationRequest, (state) => ({
      ...state,
      loading: true,
    })),
    on(NotificationActions.upsertNotificationSuccess, (state, { notification }) => ({
      ...state,
      notifications: adapter.upsertOne(notification, state.notifications),
      loading: false,
    })),
    on(NotificationActions.upsertNotificationFailure, (state) => ({
      ...state,
      loading: false,
    })),
    on(NotificationActions.softDeleteNotificationRequest, (state, { id }) => ({
      ...state,
      loading: true,
      deletingNotificationId: id,
    })),
    on(NotificationActions.softDeleteNotificationSuccess, (state, { id }) => ({
      ...state,
      notifications: adapter.removeOne(id, state.notifications),
      loading: false,
      deletingNotificationId: null,
    })),
    on(NotificationActions.softDeleteNotificationFailure, (state) => ({
      ...state,
      loading: false,
      deletingNotificationId: null,
    })),
  ),
});

export const { selectNotifications, selectSalesAreas, selectLoading, selectApplicationKeys, selectDeletingNotificationId } = notificationFeature;
